import type { Dispatch } from 'react';
import { useCallback, useMemo } from 'react';
import type { DateTime } from 'luxon';
import { useFetchFromApi } from './use-fetch-from-api';
import { useUpdateUserInformation } from './types/user-information-from-api';
import type { JSONSerializableObject } from '../../utils/json-types';
import { loginSuccess } from '../../slices/auth';
import { fetchCompanyInfo } from '../../slices/issuer-reducer';
import type { IUserInformationFromApi } from '../../slices/user';

interface IBaseVerifyUserInputParams {
  dateOfBirth: DateTime;
  companyShortName: string;
}

interface IBusinessGroupVerifyUserInputParams
  extends IBaseVerifyUserInputParams {
  businessGroup: string;
  employeeCompanyId: string;
}

interface ILast4VerifyUserInputParams extends IBaseVerifyUserInputParams {
  last4: string;
}

export enum VerifyUserInputLoginResponseMessage {
  // 200
  /** Authentication successful but issuer requires further email validation */
  VALIDATE_EMAIL = 'validateEmail',
  /** Authentication successful, user is logged in */
  EXACT_MATCH = 'exactMatch',
  /** Authentication matched multiple users, user is not logged in */
  MULTIPLE_FOUND = 'multipleFound',
  /** Authentication matched multiple users, user is not logged in */
  ENTER_SSN = 'enterSSN',
  // 400
  /** Authentication successful but blocked - not supported for Aramark */
  INVALID_LOGIN_METHOD = 'invalidLoginMethod',
  /** Unable to validate credentials due to a lock */
  LOGIN_LOCKED = 'loginLocked',
  /** Authentication successful but blocked - terminated employees with no prior purchases */
  DENIED_ACCESS = 'deniedAccess',
  /** Authentication unsuccessful - unable to verify SSN */
  VERIFY_UNABLE = 'verifyUnable',
  // 500
  GENERIC_ERROR = 'error on login attempt',
}

/** Verifies user credentials and logs in user */
export const usePostVerifyUserInputLogin = () => {
  const {
    data, error, loading, doRequest,
  } =
    useFetchFromApi<IUserInformationFromApi>({
      relativeApiUrl: '/verify_user_input',
      method: 'POST',
      initialLoadingValue: false,
    });

  const updateDataAdditionally = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (dispatch: Dispatch<any>, newData: IUserInformationFromApi) => {
      const ticker = newData.information.company.shortname.toLowerCase();
      dispatch(loginSuccess(newData.information));
      dispatch(fetchCompanyInfo({ ticker }));
    },
    [],
  );

  const { updateData, processResponse } = useUpdateUserInformation(
    updateDataAdditionally,
  );

  const sendRequestForBusinessGroup = useCallback(
    (
      {
        dateOfBirth,
        companyShortName,
        businessGroup,
        employeeCompanyId,
      }: IBusinessGroupVerifyUserInputParams,
      shouldUpdateDataAfterRequest?: boolean,
    ) =>
      doRequest(
        {
          body: {
            day: dateOfBirth.day,
            month: dateOfBirth.month,
            year: dateOfBirth.year,
            company_short_name: companyShortName,
            business_group: businessGroup,
            employee_company_id: employeeCompanyId,
          },
        },
        (responseData: JSONSerializableObject) =>
          processResponse(responseData, shouldUpdateDataAfterRequest),
      ),
    [doRequest, processResponse],
  );

  const sendRequestForLast4 = useCallback(
    (
      { dateOfBirth, companyShortName, last4 }: ILast4VerifyUserInputParams,
      shouldUpdateDataAfterRequest?: boolean,
    ) =>
      doRequest(
        {
          body: {
            day: dateOfBirth.day,
            month: dateOfBirth.month,
            year: dateOfBirth.year,
            company_short_name: companyShortName,
            last4,
            defaultCountry: 'US',
          },
        },
        (responseData: JSONSerializableObject) =>
          processResponse(responseData, shouldUpdateDataAfterRequest),
      ),
    [doRequest, processResponse],
  );

  const updateDataAfterRequest = useCallback(() => {
    if (data) {
      updateData(data);
    }
  }, [data, updateData]);

  return useMemo(
    () => ({
      data,
      error,
      loading,
      sendRequestForBusinessGroup,
      sendRequestForLast4,
      updateDataAfterRequest,
    }),
    [
      data,
      error,
      loading,
      sendRequestForBusinessGroup,
      sendRequestForLast4,
      updateDataAfterRequest,
    ],
  );
};
